import React from 'react';
import { Input, Checkbox, CheckboxGroup, Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  FilterOption,
  ModelSearchSelect,
  ResponsiveTooltip,
} from '../../../../components/ui';
import {
  SearchInputIc,
  AccountIcon,
  RefreshIc,
  ResetFiltersIc,
} from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { SubscribersFilters, SubscribersFiltersProps } from '../types';
import { PAYMENT_TYPES, ACCOUNT_OPTIONS, getPaymentTypes } from '../constants';

const FiltersSection: React.FC<SubscribersFiltersProps> = ({
  filters,
  localSearch,
  localCampaignId,
  availableModels,
  modelsLoading,
  availableAccounts,
  accountsLoading,
  onSearchChange,
  onCampaignIdChange,
  onFilterChange,
  onModelSearchChange,
  onAccountSearchChange,
  onRefresh,
  isRefreshing = false,
  hasActiveFilters = false,
  onResetFilters,
}) => {
  const { t } = useTranslations();
  return (
    <div className="flex flex-col">
      {/* Search Inputs */}
      <div className="flex items-center gap-4 mb-4 max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        <Input
          type="text"
          placeholder={'ID'}
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm',
            inputWrapper: 'rounded-lg h-9 w-[17.0625rem]',
          }}
        />

        <ModelSearchSelect
          placeholder={t('subscribers.filters.account')}
          options={[
            ...availableModels.map(
              (model) =>
                ({
                  key: model.id,
                  avatar: model.avatar,
                  username: model.username,
                  name: model.name,
                  label: `${model.name} (@${model.username})`,
                }) as FilterOption
            ),
          ]}
          selectedKey={filters.models.length > 0 ? filters.models[0] : 'all'}
          onSelectionChange={(value) => {
            const newModels = value === 'all' ? [] : [value];
            onFilterChange('models', newModels);
          }}
          onSearchChange={onModelSearchChange}
          isLoading={modelsLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
        />
        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {hasActiveFilters && onResetFilters && (
          <Tooltip content={t('subscribers.resetFilters')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Checkboxes */}
      <div className="!pr-2">
        <CheckboxGroup
          orientation="horizontal"
          value={filters.paymentTypes}
          onValueChange={(value) => {
            let nextValue = Array.isArray(value) ? [...value] : [];
            const hasPaid = nextValue.includes('paid_subscription');
            const hasFree = nextValue.includes('free_subscription');

            if (hasPaid && hasFree) {
              if (
                filters.paymentTypes.includes('paid_subscription') &&
                !filters.paymentTypes.includes('free_subscription')
              ) {
                nextValue = nextValue.filter((v) => v !== 'paid_subscription');
              } else if (
                !filters.paymentTypes.includes('paid_subscription') &&
                filters.paymentTypes.includes('free_subscription')
              ) {
                nextValue = nextValue.filter((v) => v !== 'free_subscription');
              } else {
                const newlySelected = nextValue.find((v) => !filters.paymentTypes.includes(v));
                if (newlySelected === 'paid_subscription') {
                  nextValue = nextValue.filter((v) => v !== 'free_subscription');
                } else {
                  nextValue = nextValue.filter((v) => v !== 'paid_subscription');
                }
              }
            }

            onFilterChange('paymentTypes', nextValue);
          }}
          classNames={{
            wrapper: 'gap-4',
          }}
        >
          {getPaymentTypes(t).map((type) => (
            <Checkbox key={type.key} value={type.key} classNames={{ base: '!h-6' }} size="md">
              {type.label}
            </Checkbox>
          ))}
        </CheckboxGroup>
      </div>
    </div>
  );
};

export default FiltersSection;
